import { 
    Firestore, 
    collection, 
    getDocs, 
    doc,
    writeBatch,
    DocumentSnapshot,
    Timestamp
  } from 'firebase/firestore';
  import { WorkstreamType } from '../types';
  
  interface TypeGroup {
    name: string;
    documents: {
      id: string;
      data: WorkstreamType;
      createTime?: Date;
    }[];
  }
  
  export const cleanupWorkstreamTypes = async (firestore: Firestore, workspaceId: string): Promise<void> => {
    console.log('Starting workstream types cleanup...');
    const typesRef = collection(firestore, `workspaces/${workspaceId}/workstreamTypes`);
    
    try {
      // Get all workstream types
      const snapshot = await getDocs(typesRef);
      
      // Group by name
      const typeGroups: Record<string, TypeGroup> = {};
      
      snapshot.forEach((document: DocumentSnapshot) => {
        const data = document.data() as WorkstreamType;
        const createTime = (document.get('createdAt') as Timestamp)?.toDate() || new Date();
        
        if (!typeGroups[data.name]) {
          typeGroups[data.name] = {
            name: data.name,
            documents: []
          };
        }
        
        typeGroups[data.name].documents.push({
          id: document.id,
          data,
          createTime
        });
      });
      
      // For each group with duplicates, keep the oldest one and delete the rest
      const batch = writeBatch(firestore);
      let deletionsCount = 0;
      
      for (const group of Object.values(typeGroups)) {
        if (group.documents.length > 1) {
          console.log(`Found ${group.documents.length} copies of type: ${group.name}`);
          
          // Sort by creation time if available, otherwise keep first one
          group.documents.sort((a, b) => {
            if (a.createTime && b.createTime) {
              return a.createTime.getTime() - b.createTime.getTime();
            }
            return 0;
          });
          
          // Keep the first one, delete the rest
          const toKeep = group.documents[0];
          const toDelete = group.documents.slice(1);
          
          console.log(`Keeping ${group.name} with ID: ${toKeep.id}`);
          
          for (const document of toDelete) {
            batch.delete(doc(typesRef, document.id));
            deletionsCount++;
          }
        }
      }
      
      if (deletionsCount > 0) {
        await batch.commit();
        console.log(`Successfully removed ${deletionsCount} duplicate workstream types`);
      } else {
        console.log('No duplicates found');
      }
      
    } catch (error) {
      console.error('Error during workstream types cleanup:', error);
      throw error;
    }
  };
  
  // Helper function to run the cleanup
  export const runCleanup = async (workspaceId: string): Promise<void> => {
    try {
      const { getFirestore } = await import('firebase/firestore');
      const firestore = getFirestore();
      await cleanupWorkstreamTypes(firestore, workspaceId);
    } catch (error) {
      console.error('Failed to run cleanup:', error);
      throw error;
    }
  };